import { useEffect, useMemo, useRef, useState } from "react";
import {
  PackageDetailsContainer,
  PackageDetailsContent,
  PackageDetailsHeader,
  PackageDetailsMetadata,
  PackageDetailsMetadataItem,
  PackageDetailsMetadataLabel,
  PackageDetailsMetadataValue,
  PackageDetailsNotes,
  PackageDetailsNotesLabel,
  PackageDetailsNotesText,
  PackageDetailsSwitcher,
  PackageDetailsSwitcherItem,
  Title,
} from "./Package.elements";
import { GridContainer } from "../Clips/Clip.elements";
import { usePackagesActions, usePackagesState } from "@/store";
import { FileDelimiter, FileList } from "../UploadForm/UploadForm.elements";
import ClipCard from "../Clips/ClipCard";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import NoDataState from "@/components/EmptyData/NoDataState";
import SourceFileService from "@/Service/SourceFileService";
import { useAlertActions } from "@/store/alertSlice";
import { SourceFileRow } from "./SourceFileRow";
import { formatEmbargo, isEmbargoActive } from "@/utils/embargo";
import {
  DownloadCorsError,
  createSubdirectory,
  isDirectoryPickerSupported,
  pickDownloadDirectory,
  saveFilesToDirectory,
  startBrowserDownloads,
} from "@/utils/browserDownloads";

export const PackageDetails = ({ uploadMetadataId, upload }) => {
  const [selected, setSelected] = useState("files");
  const [downloadingFileId, setDownloadingFileId] = useState(null);
  // null when idle, otherwise { label, percent } while the package is being downloaded
  const [batchProgress, setBatchProgress] = useState(null);
  const abortRef = useRef(null);
  const [folderPromptOpen, setFolderPromptOpen] = useState(false);

  const {
    fetchDetails: { status },
    packageDetails,
  } = usePackagesState();

  const { fetchPackageDetails } = usePackagesActions();
  const { showAlert } = useAlertActions();

  const normalizedSourceFiles = useMemo(
      () => upload?.sourceFiles || [],
      [upload]
  );

  const metadataItems = useMemo(
      () =>
          [
            {
              label: "Country",
              value: upload?.country,
            },
            {
              label: "State",
              value: upload?.state,
            },
            {
              label: "City",
              value: upload?.city,
            },
            {
              label: "Embargo",
              value: formatEmbargo(upload?.embargo),
              // Highlighted red while the embargo has not passed, matching the
              // badge glued to the package block.
              isAlert: isEmbargoActive(upload?.embargo),
            },
          ].filter(({ value }) => value),
      [upload]
  );

  const downloadableFiles = useMemo(
      () => normalizedSourceFiles.filter((file) => file?.id && !file?.legacy),
      [normalizedSourceFiles]
  );

  const contributorNotes = useMemo(
      () => (typeof upload?.notes === "string" ? upload.notes.trim() : ""),
      [upload]
  );

  const handleDownload = async (file) => {
    if (!file?.id || file?.legacy) {
      return;
    }

    try {
      setDownloadingFileId(file.id);

      const response = await SourceFileService.getDownloadUrl(file.id);
      const url = response?.data?.downloadUrl;

      if (url) {
        window.open(url, "_blank", "noopener");
      } else {
        showAlert("error", "Download link is unavailable");
      }
    } catch (error) {
      console.error("Failed to fetch download url", error);
      showAlert("error", "Failed to generate download link");
    } finally {
      setDownloadingFileId(null);
    }
  };

  const formatMb = (bytes) => `${Math.round((bytes || 0) / 1048576)} MB`;

  const runClassicDownloads = async (items) => {
    const started = await startBrowserDownloads(items, {
      onProgress: (done, total) =>
          setBatchProgress({ label: `Starting ${done} of ${total}…`, percent: null }),
    });
    showAlert(
        "success",
        `Started ${started} download${started === 1 ? "" : "s"} – check your browser's downloads`
    );
  };

  const packageFolderName = upload?.packageName || upload?.title || `package-${uploadMetadataId}`;

  /** Fetch the URLs and stream every file into `directory` (or classic downloads if null). */
  const runPackageDownload = async (directory) => {
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      setBatchProgress({ label: "Preparing…", percent: null });

      const response = await SourceFileService.getDownloadUrls(uploadMetadataId);
      const items = Array.isArray(response?.data) ? response.data : [];

      if (!items.length) {
        showAlert("error", "No downloadable files in this package");
        return;
      }

      if (!directory) {
        await runClassicDownloads(items);
        return;
      }

      try {
        const target = await createSubdirectory(directory, packageFolderName);
        const { saved, cancelled } = await saveFilesToDirectory(items, target, {
          signal: controller.signal,
          onProgress: ({ index, total, fileName, received, size }) =>
              setBatchProgress({
                label: size
                    ? `Saving ${index} of ${total} – ${fileName} (${formatMb(received)} / ${formatMb(size)})`
                    : `Saving ${index} of ${total} – ${fileName} (${formatMb(received)})`,
                percent: size ? Math.min(100, Math.round((received / size) * 100)) : null,
              }),
        });
        if (cancelled) {
          showAlert("info", `Download cancelled – ${saved} of ${items.length} files saved`);
        } else {
          showAlert("success", `Saved ${saved} file${saved === 1 ? "" : "s"} to "${target.name}"`);
        }
      } catch (error) {
        if (error instanceof DownloadCorsError) {
          // Bucket does not allow cross-origin reads: fall back to plain browser downloads.
          console.warn("Direct save unavailable, falling back to browser downloads", error);
          await runClassicDownloads(items);
          return;
        }
        throw error;
      }
    } catch (error) {
      console.error("Failed to download package", error);
      showAlert("error", "Failed to download package files");
    } finally {
      abortRef.current = null;
      setBatchProgress(null);
    }
  };

  const handleDownloadAll = () => {
    if (!uploadMetadataId || batchProgress) {
      return;
    }
    if (isDirectoryPickerSupported()) {
      setFolderPromptOpen(true); // explain the folder rule first; the picker opens from that dialog
    } else {
      runPackageDownload(null);
    }
  };

  /** Runs inside the dialog button click so the picker still counts as a user gesture. */
  const handleChooseFolder = async () => {
    let directory;
    try {
      directory = await pickDownloadDirectory();
    } catch (error) {
      console.warn("Folder picker failed", error);
      showAlert(
          "error",
          "Chrome couldn't use that folder. Pick or create a subfolder (for example Downloads/Clippn) and try again."
      );
      return;
    }
    if (directory === null) {
      return; // user cancelled the picker; keep the prompt open
    }
    setFolderPromptOpen(false);
    await runPackageDownload(directory);
  };

  const handleCancelDownloadAll = () => abortRef.current?.abort();

  useEffect(() => {
    fetchPackageDetails(uploadMetadataId);
  }, [fetchPackageDetails, uploadMetadataId]);

  const isLoading = status === "loading";

  return (
      <PackageDetailsContainer>
        {!isLoading && (
            <>
              {metadataItems.length > 0 && (
                  <PackageDetailsMetadata>
                    {metadataItems.map(({ label, value, isAlert }) => (
                        <PackageDetailsMetadataItem key={label}>
                          <PackageDetailsMetadataLabel $alert={isAlert}>
                            {label}
                          </PackageDetailsMetadataLabel>

                          <PackageDetailsMetadataValue $alert={isAlert}>
                            {value}
                          </PackageDetailsMetadataValue>
                        </PackageDetailsMetadataItem>
                    ))}
                  </PackageDetailsMetadata>
              )}

              {contributorNotes && (
                  <PackageDetailsNotes
                      $standalone={metadataItems.length === 0}
                  >
                    <PackageDetailsNotesLabel>
                      Contributor Notes
                    </PackageDetailsNotesLabel>

                    <PackageDetailsNotesText>
                      {contributorNotes}
                    </PackageDetailsNotesText>
                  </PackageDetailsNotes>
              )}

              <PackageDetailsHeader>
                <Stack direction="row" alignItems="center" gap={1.5} sx={{ minWidth: 0 }}>
                  <Title color="white">
                    {selected === "clips" ? "Clips" : "Files"}
                  </Title>

                  {selected === "files" && downloadableFiles.length > 1 && (
                      <Stack direction="row" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
                        <Button
                            size="small"
                            variant="outlined"
                            color="inherit"
                            onClick={handleDownloadAll}
                            disabled={!!batchProgress}
                            startIcon={
                              batchProgress
                                  ? (
                                      <CircularProgress
                                          size={14}
                                          color="inherit"
                                          variant={batchProgress.percent === null ? "indeterminate" : "determinate"}
                                          value={batchProgress.percent ?? 0}
                                      />
                                  )
                                  : <DownloadIcon fontSize="small" />
                            }
                            data-testid="download-all-button"
                            sx={{ whiteSpace: "nowrap", textTransform: "none" }}
                        >
                          {batchProgress ? batchProgress.label : `Download all (${downloadableFiles.length})`}
                        </Button>

                        {batchProgress && abortRef.current && (
                            <Button
                                size="small"
                                color="inherit"
                                onClick={handleCancelDownloadAll}
                                data-testid="download-all-cancel"
                                sx={{ textTransform: "none", opacity: 0.8 }}
                            >
                              Cancel
                            </Button>
                        )}
                      </Stack>
                  )}
                </Stack>

                <PackageDetailsSwitcher>
                  <PackageDetailsSwitcherItem
                      isActive={selected === "files"}
                      onClick={() => setSelected("files")}
                  >
                    Files
                  </PackageDetailsSwitcherItem>

                  <PackageDetailsSwitcherItem
                      isActive={selected === "clips"}
                      onClick={() => setSelected("clips")}
                  >
                    Clips
                  </PackageDetailsSwitcherItem>
                </PackageDetailsSwitcher>
              </PackageDetailsHeader>

              <Dialog
                  open={folderPromptOpen}
                  onClose={() => setFolderPromptOpen(false)}
                  maxWidth="xs"
                  fullWidth
                  data-testid="download-all-dialog"
              >
                <DialogTitle>Download {downloadableFiles.length} files</DialogTitle>
                <DialogContent>
                  <Typography variant="body2" sx={{ mb: 1.5 }}>
                    Choose where to save them. A folder named
                    {" "}<strong>{packageFolderName}</strong>{" "}
                    will be created there and every file saved into it – no further prompts.
                  </Typography>
                  <Alert severity="warning" variant="outlined" data-testid="download-all-folder-hint">
                    <strong>Chrome will not save into the Downloads folder itself</strong> – if you
                    pick it, Chrome says the folder is not available. Choose or create a
                    <strong> subfolder</strong>, for example <strong>Downloads/Clippn</strong>.
                  </Alert>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setFolderPromptOpen(false)} color="inherit">
                    Cancel
                  </Button>
                  <Button
                      onClick={handleChooseFolder}
                      variant="contained"
                      data-testid="download-all-choose-folder"
                  >
                    Choose folder…
                  </Button>
                </DialogActions>
              </Dialog>
            </>
        )}

        <PackageDetailsContent>
          {isLoading ? (
              <Stack
                  alignItems="center"
                  justifyContent="center"
                  sx={{ py: 3 }}
              >
                <CircularProgress
                    size={40}
                    disableShrink
                    color="inherit"
                />
              </Stack>
          ) : (
              <>
                {selected === "files" &&
                    (normalizedSourceFiles.length ? (
                        <FileList>
                          {normalizedSourceFiles.map((file, i) => (
                              <div
                                  key={
                                      file.id ||
                                      file.displayName ||
                                      file.originalName ||
                                      i
                                  }
                              >
                                {i !== 0 && <FileDelimiter />}

                                <SourceFileRow
                                    file={file}
                                    isDownloading={
                                        downloadingFileId === file.id
                                    }
                                    onDownload={handleDownload}
                                />
                              </div>
                          ))}
                        </FileList>
                    ) : (
                        <NoDataState />
                    ))}

                {selected === "clips" &&
                    (packageDetails.length ? (
                        <GridContainer>
                          {packageDetails.map((item) => (
                              <ClipCard
                                  key={
                                      item?.id ??
                                      item?.fileId ??
                                      JSON.stringify(item)
                                  }
                                  file={item}
                              />
                          ))}
                        </GridContainer>
                    ) : (
                        <NoDataState />
                    ))}
              </>
          )}
        </PackageDetailsContent>
      </PackageDetailsContainer>
  );
};